import React, { useContext, useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { List, Box, Typography } from "@mui/material";
import { AuthContext } from '../../FirebaseAuth';
import { FirebaseAuth } from '../../FirebaseAuth/firebase';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import PaymentIcon from '@mui/icons-material/Payment';
import SettingsIcon from '@mui/icons-material/Settings';
import TerminalIcon from '@mui/icons-material/Terminal';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NavItem from '../NavItem';

const AccountMenu = () => {
    const { t } = useTranslation();
    const { accountId } = useParams();
    const location = useLocation();
    const { userData } = useContext(AuthContext);
    const [planName, setPlanName] = useState(null);

    useEffect(() => {
        let cancelled = false;
        if (!userData.currentAccount.planId) {
            setPlanName(null);
            return;
        }
        FirebaseAuth.firestore().collection('plans').doc(userData.currentAccount.planId).get()
            .then((doc) => {
                if (!cancelled && doc.exists) setPlanName(doc.data().name);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [userData.currentAccount.planId]);

    const isActive = (path) => location.pathname === path;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, m: 2, p: 1.25,
                    borderRadius: 2, backgroundColor: (theme) => theme.custom.card,
                    boxShadow: (theme) => theme.custom.cardShadow,
                }}
            >
                <Box
                    sx={{
                        width: 34, height: 34, borderRadius: '9px', flexShrink: 0,
                        backgroundColor: (theme) => theme.palette.primary.main,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    <LocationOnIcon sx={{ color: '#fff', fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        noWrap
                        sx={{ fontWeight: 700, fontSize: 14, color: (theme) => theme.custom.text }}
                    >
                        {userData.currentAccount.name}
                    </Typography>
                    {planName && (
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-block', mt: 0.4, fontSize: 10, fontWeight: 700,
                                letterSpacing: '0.04em', textTransform: 'uppercase',
                                color: (theme) => theme.palette.primary.main,
                                backgroundColor: (theme) => theme.custom.planBadgeBg,
                                px: 1, borderRadius: 999,
                            }}
                        >
                            {planName}
                        </Box>
                    )}
                </Box>
            </Box>

            <List sx={{ px: 1 }}>
                <NavItem
                    to={'/account/' + accountId + '/'}
                    icon={<DashboardIcon />}
                    label={t('nav.dashboards')}
                    active={isActive('/account/' + accountId + '/')}
                />
                <NavItem
                    to={'/account/' + accountId + '/installation'}
                    icon={<TerminalIcon />}
                    label={t('nav.installation')}
                    active={isActive('/account/' + accountId + '/installation')}
                />
                {userData.currentAccount.role === 'admin' && (
                    <>
                        <Typography
                            sx={{
                                px: 1.5, pt: 1.75, pb: 0.75, fontSize: 10, fontWeight: 700,
                                color: (theme) => theme.custom.mutedFaint,
                                textTransform: 'uppercase', letterSpacing: '0.08em',
                            }}
                        >
                            {t('nav.admin')}
                        </Typography>
                        <NavItem
                            to={'/account/' + accountId + '/settings'}
                            icon={<SettingsIcon />}
                            label={t('nav.settings')}
                            active={isActive('/account/' + accountId + '/settings')}
                        />
                        <NavItem
                            to={'/account/' + accountId + '/users'}
                            icon={<PeopleIcon />}
                            label={t('nav.users')}
                            active={isActive('/account/' + accountId + '/users')}
                        />
                        <NavItem
                            to={'/account/' + accountId + '/billing'}
                            icon={<PaymentIcon />}
                            label={t('nav.billing')}
                            active={isActive('/account/' + accountId + '/billing')}
                        />
                    </>
                )}
            </List>
        </Box>
    )
}

export default AccountMenu;
